// PDF de los programas anuales (lote C, M48), con el estilo de los registros firmados (firmas-pdf.ts): banda oscura,
// campos en dos columnas, tabla del cronograma, firmas con trazo, nombre, cédula, fecha y hora del servidor y SHA-256.
//  - programa: la versión aprobada con sus firmas (o la nota del escaneo, si se firmó en papel); en firma o borrador
//    lleva la marca correspondiente;
//  - formato para firma manual: el contenido congelado con los recuadros de firma vacíos (se imprime, se firma y se
//    sube el escaneo); lleva la huella del contenido para cotejarlo;
//  - informe de ejecución: cada actividad con su fecha planificada, la fecha real y el respaldo, y el estado calculado.
// Los plazos se presentan como "sugeridos por StorSafe, no normativos".

import { jsPDF } from 'jspdf';
import { paraPdf } from '../pe/pe-plan-pdf';
import { fechaCorta, type EstadoActividad, ETIQUETA_ESTADO_ACTIVIDAD, type ResultadoPrograma } from './prog-logica';
import {
  CITA_MATRIZ_SUSTANCIAS, CONFIG, ETIQUETA_ESTADO_ITEM, ETIQUETA_ESTADO_VERSION, ETIQUETA_EXAMEN, ETIQUETA_ROL, NOTA_PERIODICIDAD, NOTA_PLAZOS,
  ROLES_FIRMA, TEXTO_FIRMA_PROGRAMA, type ContenidoPrograma, type EstadoVersion, type MetodoFirma, type RolFirmaPrograma,
} from './prog-tipos';

export interface FirmaProgramaPdf { rol: RolFirmaPrograma; nombre: string; cedula: string; firmadoEn: string; hash: string; imagen: string | null }

export interface DatosProgramaPDF {
  contenido: ContenidoPrograma;
  hash: string | null;
  estado: EstadoVersion;
  metodo: MetodoFirma | null;
  aprobadoEn: string | null;
  firmas: FirmaProgramaPdf[];
  blanco: boolean;
  fecha: Date;
}

const MARGEN = 15;
const ANCHO = 210 - MARGEN * 2;
const ALTO_UTIL = 297 - 20;
const ARRIBA = 22;

export const fechaHora = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('es-EC', { timeZone: 'America/Guayaquil', dateStyle: 'short', timeStyle: 'medium' }) : '—';

export function nombreArchivoPrograma(c: Pick<ContenidoPrograma, 'tipo' | 'anio' | 'version'>, sufijo: string): string {
  const base = CONFIG[c.tipo].corto.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '');
  return `${base}_${c.anio}_v${c.version}${sufijo ? `_${sufijo}` : ''}.pdf`;
}

function lienzo(titulo: string, subtitulo: string, empresa: string) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const T = (s: string) => paraPdf(s);
  const st = { y: ARRIBA };
  const nuevaPagina = () => { doc.addPage(); st.y = ARRIBA; };
  const espacio = (alto: number) => { if (st.y + alto > ALTO_UTIL) nuevaPagina(); };
  const texto = (s: string, tam = 9, o: { negrita?: boolean; gris?: boolean } = {}) => {
    doc.setFont('helvetica', o.negrita ? 'bold' : 'normal'); doc.setFontSize(tam);
    if (o.gris) doc.setTextColor(100, 116, 139); else doc.setTextColor(15, 23, 42);
    (doc.splitTextToSize(T(s), ANCHO) as string[]).forEach(l => { espacio(tam * 0.45); doc.text(l, MARGEN, st.y); st.y += tam * 0.45; });
    doc.setTextColor(15, 23, 42);
  };
  const seccion = (s: string) => {
    espacio(14); st.y += 3;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11.5); doc.setTextColor(29, 111, 163);
    doc.text(T(s), MARGEN, st.y);
    doc.setDrawColor(203, 213, 225); doc.line(MARGEN, st.y + 1.5, MARGEN + ANCHO, st.y + 1.5);
    st.y += 6.5; doc.setTextColor(15, 23, 42);
  };
  const campos = (pares: [string, string][]) => {
    for (const [k, v] of pares) {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
      const valor = doc.splitTextToSize(T(v || '—'), ANCHO - 52) as string[];
      espacio(valor.length * 4.2 + 2);
      const y0 = st.y;
      doc.setFont('helvetica', 'bold'); doc.text(T(`${k}:`), MARGEN, y0);
      doc.setFont('helvetica', 'normal'); valor.forEach((l, n) => doc.text(l, MARGEN + 52, y0 + n * 4.2));
      st.y += (valor.length - 1) * 4.2 + 5.2;
    }
  };
  /** Tabla simple: columnas con ancho; cada celda se parte en líneas. */
  const tabla = (cols: [string, number][], filas: string[][]) => {
    const cabecera = () => {
      espacio(8);
      doc.setFillColor(241, 245, 249); doc.rect(MARGEN, st.y - 4, ANCHO, 6, 'F');
      doc.setFont('helvetica', 'bold'); doc.setFontSize(7.8);
      let x = MARGEN + 1;
      cols.forEach(([t, w]) => { doc.text(T(t), x, st.y); x += w; });
      st.y += 4;
    };
    cabecera();
    filas.forEach(f => {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7.8);
      const partes = f.map((c, i) => doc.splitTextToSize(T(c || '—'), cols[i][1] - 2) as string[]);
      const alto = Math.max(...partes.map(p => p.length)) * 3.6 + 2.5;
      if (st.y + alto > ALTO_UTIL) { nuevaPagina(); cabecera(); }
      let x = MARGEN + 1;
      partes.forEach((p, i) => { p.forEach((l, n) => doc.text(l, x, st.y + 3 + n * 3.6)); x += cols[i][1]; });
      doc.setDrawColor(226, 232, 240); doc.line(MARGEN, st.y + alto, MARGEN + ANCHO, st.y + alto);
      st.y += alto + 1;
    });
    if (!filas.length) texto('Sin actividades.', 8.5, { gris: true });
  };
  doc.setFillColor(15, 23, 42); doc.rect(0, 0, 210, 46, 'F');
  doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(15);
  (doc.splitTextToSize(T(titulo), ANCHO) as string[]).slice(0, 2).forEach((l, i) => doc.text(l, MARGEN, 18 + i * 7));
  doc.setFont('helvetica', 'normal'); doc.setFontSize(10.5);
  doc.text(T(empresa || 'Empresa'), MARGEN, 34);
  doc.setFontSize(9.5);
  doc.text(T(subtitulo), MARGEN, 41);
  doc.setTextColor(15, 23, 42);
  st.y = 56;
  const pie = (textoPie: string) => {
    const paginas = doc.getNumberOfPages();
    for (let p = 1; p <= paginas; p++) {
      doc.setPage(p);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(100, 116, 139);
      doc.text(T(textoPie), MARGEN, 290);
      doc.text(T(`Página ${p} de ${paginas}`), 210 - MARGEN, 290, { align: 'right' });
      doc.setTextColor(15, 23, 42);
    }
  };
  return { doc, T, st, espacio, texto, seccion, campos, tabla, pie };
}

const horas = (h: number | null) => (h === null ? '—' : `${String(h).replace('.', ',')} h`);

export function generarProgramaPDF(d: DatosProgramaPDF): jsPDF {
  const c = d.contenido;
  const cfg = CONFIG[c.tipo];
  const marca = d.blanco ? 'Formato para firma manual' : d.estado === 'aprobada' ? 'Versión aprobada y firmada'
    : d.estado === 'reemplazada' ? 'Versión reemplazada por una posterior' : d.estado === 'en_firma' ? 'Versión en firma (sin aprobar)' : 'Borrador (sin aprobar)';
  const L = lienzo(`${cfg.titulo} ${c.anio}`, `Versión ${c.version} · ${marca}`, c.empresa);
  const { doc, T, st, espacio, texto, seccion, campos, tabla } = L;

  campos([['Año', String(c.anio)], ['Versión', `${c.version} (${ETIQUETA_ESTADO_VERSION[d.estado]})`], ['Base normativa', cfg.norma],
    ...(c.tipo === 'sustancias' ? [['Actividades', CITA_MATRIZ_SUSTANCIAS] as [string, string]] : [])]);
  seccion('Contenido del programa');
  campos([
    ['Objetivos', c.objetivos], [c.tipo === 'capacitacion' ? 'Alcance y diagnóstico de necesidades' : c.tipo === 'inspecciones' ? 'Alcance y áreas a inspeccionar'
      : 'Alcance y diagnóstico', c.alcance_diagnostico],
    [c.tipo === 'inspecciones' ? 'Lista de verificación' : 'Metodología', c.metodologia], ['Responsables', c.responsables], ['Recursos y material', c.recursos],
    ...(c.tipo === 'examenes' ? [['Periodicidad', `${c.ajustes.periodicidad_meses ?? 12} meses`] as [string, string]] : []),
  ]);
  if (c.tipo === 'examenes') texto(NOTA_PERIODICIDAD, 7.5, { gris: true });

  seccion(c.tipo === 'examenes' ? 'Cronograma de exámenes' : c.tipo === 'inspecciones' ? 'Cronograma de inspecciones' : 'Cronograma de actividades');
  const conNumero = c.tipo === 'psicosocial' || c.tipo === 'sustancias';
  const numero = (a: ContenidoPrograma['actividades'][number]) => String(conNumero && a.numero_lista ? a.numero_lista : a.orden);
  if (c.tipo === 'examenes') {
    tabla([['N.', 8], ['Trabajador', 62], ['Examen', 24], ['Fecha planificada', 28], ['Responsable', 58]],
      c.actividades.map(a => [String(a.orden), a.destinatarios_texto, a.tipo_examen ? ETIQUETA_EXAMEN[a.tipo_examen] : '—', fechaCorta(a.fecha_planificada), a.responsable]));
  } else if (c.tipo === 'inspecciones') {
    tabla([['N.', 8], ['Inspección (plantilla · área)', 84], ['Fecha', 22], ['Responsable', 38], ['Frecuencia', 28]],
      c.actividades.map(a => [String(a.orden), a.actividad, fechaCorta(a.fecha_planificada), a.responsable, a.notas.replace(/^Programación /, '')]));
  } else if (c.tipo === 'sustancias') {
    tabla([['Act.', 10], ['Actividad', 70], ['Fecha', 20], ['Responsable', 34], ['Indicador', 46]],
      c.actividades.map(a => [numero(a), a.actividad, fechaCorta(a.fecha_planificada), a.responsable, a.indicador]));
  } else {
    tabla([[conNumero ? 'Act.' : 'N.', 10], ['Actividad', 62], ['Destinatarios', 34], ['Fecha', 20], ['Responsable', 34], ['Duración', 20]],
      c.actividades.map(a => [numero(a), a.actividad, a.destinatarios_texto, fechaCorta(a.fecha_planificada), a.responsable, horas(a.duracion_horas)]));
  }

  seccion('Firmas de responsabilidad');
  espacio(44);
  const anchoCol = (ANCHO - 6) / 2;
  const y0 = st.y + 1;
  ROLES_FIRMA.forEach((rol, k) => {
    const x = MARGEN + k * (anchoCol + 6);
    const f = d.blanco ? undefined : d.firmas.find(z => z.rol === rol);
    doc.setDrawColor(148, 163, 184); doc.rect(x, y0, anchoCol, 22);
    if (f?.imagen) { try { doc.addImage(f.imagen, 'PNG', x + 1, y0 + 0.5, anchoCol - 2, 21); } catch { /* imagen dañada: queda el recuadro */ } }
    else if (!d.blanco && d.metodo === 'papel') { doc.setFontSize(6.5); doc.setTextColor(100, 116, 139); doc.text(T('En el formato escaneado'), x + 2, y0 + 12); doc.setTextColor(15, 23, 42); }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.text(T(ETIQUETA_ROL[rol]), x, y0 + 26);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    doc.text(T(f ? `${f.nombre} · C.I. ${f.cedula}` : d.blanco ? 'Nombre y C.I.:' : d.metodo === 'papel' ? 'Firmado en papel' : 'Sin firma'), x, y0 + 30);
    if (f) {
      doc.setFontSize(6.3); doc.setTextColor(100, 116, 139);
      doc.text(T(`${fechaHora(f.firmadoEn)} · SHA-256 ${f.hash.slice(0, 24)}…`), x, y0 + 34);
      doc.setTextColor(15, 23, 42);
    }
  });
  st.y = y0 + 40;

  seccion('Integridad del programa');
  if (d.hash) texto(`Huella del contenido (SHA-256): ${d.hash}`, 7.5);
  if (d.blanco) texto('Imprima este formato, recoja las firmas a mano y suba el escaneo firmado en StorSafe, en la misma versión del programa.', 8);
  else texto(d.aprobadoEn ? `Aprobado el ${fechaHora(d.aprobadoEn)} (hora del servidor).` : 'Esta versión todavía no está aprobada.', 8);
  texto(TEXTO_FIRMA_PROGRAMA, 7.5, { gris: true });
  texto(NOTA_PLAZOS, 7.5, { gris: true });
  L.pie(`${cfg.corto} ${c.anio} · versión ${c.version} · generado ${d.fecha.toLocaleDateString('es-EC')}`);
  return doc;
}

export interface FilaInforme { actividad: string; numero: number | null; destinatarios: string; planificada: string; estado: EstadoActividad;
  ejecutada: string | null; respaldo: string }

export interface DatosInformePDF {
  contenido: ContenidoPrograma;     // de la versión aprobada
  aprobadoEn: string | null;
  filas: FilaInforme[];
  resultado: ResultadoPrograma;
  fecha: Date;
}

export function generarInformeEjecucionPDF(d: DatosInformePDF): jsPDF {
  const c = d.contenido;
  const cfg = CONFIG[c.tipo];
  const L = lienzo(`Informe de ejecución: ${cfg.corto.toLowerCase()} ${c.anio}`, `Versión ${c.version} aprobada el ${fechaCorta(d.aprobadoEn)}`, c.empresa);
  const { texto, seccion, campos, tabla } = L;
  const ejecutadas = d.filas.filter(f => f.estado === 'ejecutada').length;
  campos([
    ['Programa', cfg.titulo], ['Base normativa', cfg.norma],
    ...(c.tipo === 'sustancias' ? [['Lista de actividades', CITA_MATRIZ_SUSTANCIAS] as [string, string]] : []),
    ['Actividades', `${d.filas.length} en el cronograma; ${ejecutadas} ejecutadas`],
    ['Estado calculado', `${ETIQUETA_ESTADO_ITEM[d.resultado.estado]} (${cfg.item.replace('-0', ' ').replace('-', ' ')})`],
  ]);
  texto(d.resultado.resumen, 8.5);
  seccion('Ejecución por actividad');
  tabla([['Act.', 10], ['Actividad', 54], ['Planificada', 20], ['Estado', 26], ['Ejecutada', 20], ['Respaldo', 50]],
    d.filas.map((f, i) => [String(f.numero ?? i + 1), `${f.actividad}${f.destinatarios ? ` · ${f.destinatarios}` : ''}`, fechaCorta(f.planificada),
      ETIQUETA_ESTADO_ACTIVIDAD[f.estado], fechaCorta(f.ejecutada), f.respaldo || '—']));
  seccion('Criterios');
  texto(NOTA_PLAZOS, 8, { gris: true });
  texto('Una actividad cuenta como ejecutada cuando tiene fecha real y respaldo: un archivo o foto de evidencia, o un registro vinculado de StorSafe (capacitación realizada, examen médico hecho, evidencia del seguimiento del programa psicosocial o inspección firmada de la misma programación).', 8, { gris: true });
  L.pie(`Informe de ejecución ${c.anio} · generado ${d.fecha.toLocaleDateString('es-EC')}`);
  return L.doc;
}
